import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import waiting from "../../assets/images/waiting.svg";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as utils from "../../components/utils.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';

const Configurations_Screen = () => {
    const { currentUser } = useContext(UserContext);
    const [configuration, setConfiguration] = useState(null);
    const [originalConfiguration, setOriginalConfiguration] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { t } = useTranslation();

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    // Fallback descriptions for configuration fields
    const getFieldDescription = (fieldName) => {
        const currentLanguage = i18n.language || 'it';
        
        const descriptions = {
            it: {
                'ShowType': 'Mostra il pulsante Tipo "Interno/Esterno"',
                'ACPM': 'Mostra il pulsante ACPM nel campo note',
                'ShowGender': 'Mostra il pulsante Genere "Uomo/Donna"',
                'AllowMultipleIndications': 'Consente di abilitare/disabilitare indicazioni multiple nell\'interfaccia utente'
            },
            en: {
                'ShowType': 'Show type bit "Internal/External"',
                'ACPM': 'Show ACPM bit in notes field',
                'ShowGender': 'Show gender bit "Male/Female"',
                'AllowMultipleIndications': 'Allow/disable multiple indications in the user interface'
            }
        };
        
        return descriptions[currentLanguage]?.[fieldName] || descriptions['it'][fieldName] || fieldName;
    };

    const setBreadCrumb = () => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin",
            name: t('admin', { ns: 'labels' })
        },
        {
            link: "/admin/configurations",
            name: t('configurations', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    }

    useEffect(() => {
        setBreadCrumb();
        menu.write({ user: currentUser() });
        loadConfiguration();
    }, []);

    const loadConfiguration = async () => {
        setIsLoading(true);
        try {
            const response = await api.sendMessage('darm/configurations/get', {
                user: currentUser(),
                body: {}
            });

            if (response.success) {
                setConfiguration(response.data);
                setOriginalConfiguration(JSON.parse(JSON.stringify(response.data))); // Deep copy for comparison
                setHasUnsavedChanges(false);
            } else {
                console.error('Error from API:', response);
                showError(t('errorLoadingConfiguration', { ns: 'labels' }));
            }
        } catch (error) {
            console.error('Error loading configuration:', error);
            showError(t('errorLoadingConfiguration', { ns: 'labels' }));
        } finally {
            setIsLoading(false);
        }
    };

    const toggleField = (fieldName) => {
        if (!configuration) return;

        // Update local state only (no API call)
        const updatedConfig = { ...configuration };
        if (updatedConfig.config && typeof updatedConfig.config === 'object') {
            updatedConfig.config = { ...updatedConfig.config };
            updatedConfig.config[fieldName] = !updatedConfig.config[fieldName];
        } else {
            // Fallback for string format
            const configData = JSON.parse(updatedConfig.Config || '{}');
            configData[fieldName] = !configData[fieldName];
            updatedConfig.Config = JSON.stringify(configData);
        }
        setConfiguration(updatedConfig);
        
        // Check if there are unsaved changes
        checkForUnsavedChanges(updatedConfig);
    };

    const checkForUnsavedChanges = (currentConfig) => {
        if (!originalConfiguration) {
            setHasUnsavedChanges(false);
            return;
        }

        // Compare current configuration with original
        const currentConfigData = currentConfig.config || JSON.parse(currentConfig.Config || '{}');
        const originalConfigData = originalConfiguration.config || JSON.parse(originalConfiguration.Config || '{}');

        const hasChanges = Object.keys(currentConfigData).some(key => 
            currentConfigData[key] !== originalConfigData[key]
        );

        setHasUnsavedChanges(hasChanges);
    };

    const saveConfiguration = async () => {
        if (!configuration || !hasUnsavedChanges) {
            showInfo(t('noChangesToSave', { ns: 'labels' }));
            return;
        }

        setIsSaving(true);
        try {
            // Get current configuration data
            const configData = configuration.config || JSON.parse(configuration.Config || '{}');
            
            const response = await api.sendMessage('darm/configurations/save', {
                user: currentUser(),
                body: { 
                    configuration: configData
                }
            });

            if (response.success) {
                // Update original configuration to reflect saved state
                setOriginalConfiguration(JSON.parse(JSON.stringify(configuration)));
                setHasUnsavedChanges(false);
                showSuccess(t('configurationSavedSuccessfully', { ns: 'labels' }));
            } else {
                console.error('Save configuration API error:', response);
                showError(t('errorSavingConfiguration', { ns: 'labels' }));
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            showError(t('errorSavingConfiguration', { ns: 'labels' }));
        } finally {
            setIsSaving(false);
        }
    };

    const showSuccess = (message) => {
        withReactContent(Swal).fire({
            title: t('success', { ns: 'labels' }),
            icon: "success",
            text: message,
            showConfirmButton: false,
            timer: 2000
        });
    };

    const showError = (message) => {
        withReactContent(Swal).fire({
            title: t('error', { ns: 'labels' }),
            icon: "error",
            text: message,
            showConfirmButton: true,
            allowOutsideClick: false,
            showCloseButton: true,
        });
    };

    const showInfo = (message) => {
        withReactContent(Swal).fire({
            title: t('info', { ns: 'labels' }),
            icon: "info",
            text: message,
            showConfirmButton: true,
            timer: 2000
        });
    };

    const renderConfiguration = () => {
        if (isLoading) {
            return (
                <div className="text-center py-4">
                    <img src={waiting} alt="Loading..." style={{ width: '50px' }} />
                    <p className="mt-2">{t('loadingConfiguration', { ns: 'labels' })}</p>
                </div>
            );
        }

        if (!configuration) {
            return (
                <div className="text-center py-4">
                    <i className="fa fa-info-circle fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">{t('noConfigurationFound', { ns: 'labels' })}</h5>
                    <button 
                        className="btn btn-primary mt-3" 
                        onClick={loadConfiguration}
                    >
                        <i className="fa fa-refresh me-2"></i>
                        {t('retry', { ns: 'labels' })}
                    </button>
                </div>
            );
        }

        let configData = {};
        try {
            // The config field is already an object, not a JSON string!
            if (configuration.config && typeof configuration.config === 'object') {
                configData = configuration.config;
            } else if (configuration.Config && typeof configuration.Config === 'string') {
                // Fallback for JSON string format
                configData = JSON.parse(configuration.Config);
            } else {
                configData = {};
            }
        } catch (error) {
            console.error('Error processing configuration data:', error);
            configData = {};
        }

        return (
            <div className="card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1">
                                <i className="fa fa-cog me-2 text-primary"></i>
                                {t('configurationSettings', { ns: 'labels' }) !== 'configurationSettings' ? t('configurationSettings', { ns: 'labels' }) : 'Impostazioni di Configurazione'}
                            </h5>
                            <small className="text-muted">
                                {configuration.healthAuthority || configuration.HealthAuthority || t('currentUserConfiguration', { ns: 'labels' })}
                            </small>
                        </div>
                        {isSaving && (
                            <div className="d-flex align-items-center text-primary">
                                <img src={waiting} alt="Saving..." style={{ width: '20px' }} className="me-2" />
                                <small>{t('saving', { ns: 'labels' })}</small>
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {Object.keys(configData).length === 0 ? (
                        <div className="text-center py-5">
                            <div className="text-muted mb-3">
                                <i className="fa fa-cog" style={{ fontSize: '64px', opacity: 0.3 }}></i>
                            </div>
                            <h5 className="text-muted mb-3">
                                {t('noConfigurationFound', { ns: 'labels' })}
                            </h5>
                            <p className="text-muted">
                                {t('noFieldsInConfiguration', { ns: 'labels' })}
                            </p>
                            <button 
                                className="btn btn-primary mt-3" 
                                onClick={loadConfiguration}
                            >
                                <i className="fa fa-refresh me-2"></i>
                                {t('retry', { ns: 'labels' })}
                            </button>
                        </div>
                    ) : (
                        <div className="row">
                            {Object.entries(configData).map(([fieldName, fieldValue]) => (
                                <div key={fieldName} className="col-12 col-lg-6 col-xl-4 mb-4">
                                    <div className="card h-100 border-light shadow-sm" style={{ transition: 'all 0.3s ease' }}>
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="flex-grow-1">
                                                    <h6 className="fw-semibold mb-2 text-dark">{fieldName}</h6>
                                                    <p className="text-muted small mb-2" style={{ lineHeight: '1.4' }}>
                                                        {getFieldDescription(fieldName)}
                                                    </p>
                                                    <div className="d-flex align-items-center">
                                                        <span className={`badge ${fieldValue ? 'bg-success' : 'bg-secondary'} me-2`} style={{ fontSize: '0.7em' }}>
                                                            <i className={`fa ${fieldValue ? 'fa-check' : 'fa-times'} me-1`}></i>
                                                            {typeof fieldValue === 'boolean' 
                                                                ? (fieldValue ? t('enabled', { ns: 'labels' }) : t('disabled', { ns: 'labels' }))
                                                                : String(fieldValue)
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ms-3">
                                                    {typeof fieldValue === 'boolean' ? (
                                                        <div className="form-check form-switch">
                                                            <input 
                                                                className="form-check-input" 
                                                                type="checkbox" 
                                                                id={`switch-${fieldName}`}
                                                                checked={fieldValue}
                                                                onChange={() => toggleField(fieldName)}
                                                                disabled={isSaving}
                                                                style={{ transform: 'scale(1.2)' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className="badge bg-light text-dark border">{String(fieldValue)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {/* CSS for enhanced styling */}
            <style>
                {`
                .icon-box {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                .bg-primary-light {
                    background-color: rgba(13, 110, 253, 0.1) !important;
                }
                .bg-success-light {
                    background-color: rgba(25, 135, 84, 0.1) !important;
                }
                .bg-warning-light {
                    background-color: rgba(255, 193, 7, 0.1) !important;
                }
                .bg-info-light {
                    background-color: rgba(13, 202, 240, 0.1) !important;
                }
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,0.05);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
                    transition: all 0.3s ease;
                }
                .card:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    transform: translateY(-1px);
                }
                .form-check-input:checked {
                    background-color: #28a745;
                    border-color: #28a745;
                }
                .form-check-input:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }
                .page-header {
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid rgba(0,0,0,0.1);
                }
                .page-title {
                    color: #343a40;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }
                .page-description {
                    font-size: 0.95rem;
                    margin-bottom: 0;
                }
                `}
            </style>

            {/* Breadcrumb managed by centralized component */}
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
            </div>

            <div className="container-fluid">
                {/* Page Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="page-header">
                            <div className="row align-items-center">
                                <div className="col-sm-6">
                                    <h1 className="page-title">
                                        <i className="fa fa-cogs me-2"></i>
                                        {t('systemConfigurations', { ns: 'labels' })}
                                    </h1>
                                    <p className="page-description text-muted">
                                        {t('manageSystemConfigurations', { ns: 'labels' })}
                                    </p>
                                </div>
                                <div className="col-sm-6">
                                    <div className="text-sm-end">
                                        <div className="d-flex gap-3 justify-content-end align-items-center">
                                            {/* Unsaved Changes Indicator */}
                                            {hasUnsavedChanges && !isSaving && (
                                                <span 
                                                    className="text-warning d-flex align-items-center" 
                                                    style={{ fontSize: '12px' }}
                                                    title={t('unsavedChanges', { ns: 'labels' })}
                                                >
                                                    <i className="fas fa-circle me-1"></i>
                                                    {t('unsavedChanges', { ns: 'labels' })}
                                                </span>
                                            )}
                                            
                                            {/* Save Button */}
                                            <button 
                                                type="button" 
                                                className={`btn ${hasUnsavedChanges ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={saveConfiguration} 
                                                disabled={!hasUnsavedChanges || isSaving}
                                                title={t('saveConfiguration', { ns: 'labels' })}
                                            >
                                                <i className={`fa-solid ${isSaving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
                                                {t('save', { ns: 'labels' })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Row */}
                {configuration && (
                    <div className="row mb-4">
                        <div className="col-xl-3 col-lg-6 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-primary">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.keys(configData).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('totalSettings', { ns: 'labels' }) !== 'totalSettings' ? t('totalSettings', { ns: 'labels' }) : 'Impostazioni Totali'}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-primary-light">
                                            <i className="fa fa-cog text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-success">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.values(configData).filter(v => v === true).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('enabled', { ns: 'labels' })}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-success-light">
                                            <i className="fa fa-toggle-on text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-warning">
                                                {(() => {
                                                    const configData = configuration.config || JSON.parse(configuration.Config || '{}');
                                                    return Object.values(configData).filter(v => v === false).length;
                                                })()}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('disabled', { ns: 'labels' })}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-warning-light">
                                            <i className="fa fa-toggle-off text-warning"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h2 className="mb-1 text-info">
                                                {configuration.healthAuthority || configuration.HealthAuthority || 'N/A'}
                                            </h2>
                                            <p className="mb-0 text-muted">
                                                {t('healthAuthority', { ns: 'labels' }) !== 'healthAuthority' ? t('healthAuthority', { ns: 'labels' }) : 'Ente Sanitario'}
                                            </p>
                                        </div>
                                        <div className="icon-box bg-info-light">
                                            <i className="fa fa-hospital text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Configuration Content */}
                <div className="row justify-content-center">
                    <div className="col-12">
                        {renderConfiguration()}
                    </div>
                </div>
            </div>
        </>
    );
};

export { Configurations_Screen }; 